// 📦 Import de React et des hooks nécessaires
import React, { useState, useEffect } from "react";
import { Switch, Box } from "@mui/material";

// 🎨 Import de Material-UI
import { useMediaQuery } from "@mui/material";
import SnackbarFeedback from "../../pages/SnackbarFeedback.js";

// Constants
import { getMomentColor } from "../../constants/moments.js";
import { CATEGORY_COLORS, CATEGORY_ICONS } from "../../constants/categories.js";

// 🌐 Contextes (sourcs de données globales)
import { useLanguage } from "../../context/LanguageContext.js";
import { useGroupe } from "../../context/GroupeContext.js";
import { useMenu } from "../../context/MenuContext.js";

import { useCustomBlocks } from "../../hooks/useCustomBlocks.js";
import { useContacts } from "../../hooks/useContacts.js";
import { useAdminRole } from "../../hooks/useAdminRole.js";
import { useContactsPeriodeDominante } from "../../hooks/useContactsPeriodeDominante.js";

// 🛠️ Fonction utilitaires
import { buildColumns } from "../../utils/buildColumns.js";
import { renderEditableCell } from "../../utils/renderEditableCell.js";
import { getRowClassName } from "../../utils/getRowClassName.js";
import { getTranslatedTheme } from "../../utils/themeUtils.js";
import { createItemRowUpdate } from "../../utils/createItemRowUpdate.js";
import { handlePrint } from "../../utils/printUtilsListItems.js";
import { mergeItemsSources } from "../../utils/mergeItemsSources.js";
import { buildTableRows } from "../../utils/buildTableRows.js";

import BoutonPartagerListeWhatsApp from "../../components/BoutonPartagerListeWhatsApp.js";

import { useLocation } from "react-router-dom";
import { logDev } from "../../utils/logger.js";

// 📋 Tableau principal
import TableauListeItems from "./TableauListeItems.js";
import ListViewToggleButton from "./ListViewToggleButton";

// 📡 HTTP
import axios from "axios";

// ------------------------------------------------------------
// ListItems : construit la liste de course fusionnée
// ------------------------------------------------------------
const ListItems = ({ headerHeight }) => {

  const { t } = useLanguage();
  const { groupeActif, groupeId, nom, emoji, couleur } = useGroupe();
  const { contacts, periodeDominante } = useContacts(groupeId);

  // 🌐 Menus journaliers (source principale)
  const { itemsMenus } = useMenu();
  logDev("🧨 itemsMenus RAW =", itemsMenus);
  logDev("🧨 itemsMenus keys =", Object.keys(itemsMenus || {}));

   // 🌐 CustomBlocks (items manuels)
  const { customBlocks, getAllCustomItems } = useCustomBlocks();

  const isMobile = useMediaQuery("(max-width:600px)");

  const role = useAdminRole(groupeId);
  const isAdmin = role === "admin";

  // 📍 Route actuelle (pour recharger si navigation)
  const location = useLocation();

  // 🧮 Contacts filtrés selon période dominante
  const { contactsDominante } = useContactsPeriodeDominante(contacts, {
    winningKey: periodeDominante,
    onlyValidEmails: true,
    fallbackAll: false
  });

  // 📧 Emails utiles pour impression
  const emailsPeriodeDominante = (contactsDominante || [])
    .map(c => c.mail?.trim())
    .filter(Boolean);

  const [articlesById, setArticlesById] = useState({});
  const [visibleContactIds, setVisibleContactIds] = useState([]);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [vueCards, setVueCards] = useState(true);
  const [categoriesCards, setCategoriesCards] = useState([]);
  const [search, setSearch] = useState("");
  const [categorieFiltre, setCategorieFiltre] = useState("Tout");

  // ------------------------------------------------------------
  // 🔄 CHARGEMENT PRINCIPAL : fusionne menus + custom + saisies
  // ------------------------------------------------------------
  const chargerRows = async () => {
    try {
      // 1️⃣ Charger les saisies (quantités par contact)
      const resSaisies = await axios.get(`${process.env.REACT_APP_API}/saisie`, {
        params: { groupeId }
      });
      const saisies = resSaisies.data || [];

      // 2️⃣ Charger les articles 
      const resArticles = await axios.get(`${process.env.REACT_APP_API}/articles`, {
        params: { groupeId }
      });
      const articles = resArticles.data || [];

       // Indexation rapide par ID
      const articlesByIdLocal = {};
      articles.forEach(a => {
        articlesByIdLocal[a._id] = a;
      });
      setArticlesById(articlesByIdLocal);

      // 🩹 Si la collection articles est vide, reconstruire depuis itemsMenus
      if (articles.length === 0) {
      logDev("⚠️ Aucun article trouvé en base — reconstruction depuis itemsMenus");

      const allItems = Object.entries(itemsMenus)
        .flatMap(([date, repasArray]) =>
          repasArray.flatMap(r =>
          r.categories.flatMap(c =>
          c.items
        )
      )
    );

    allItems.forEach(it => {
      const articleId =
        it.articleId && typeof it.articleId === "object"
          ? it.articleId._id
          : it.articleId;

      if (!articlesByIdLocal[articleId]) {
        articlesByIdLocal[articleId] = {
         _id: articleId,
          name: it.articleId?.name || it.name || it.quoi || "Sans nom",
          categorie: it.categorie,
          moment: it.moment
        };
      }
    });

    setArticlesById({ ...articlesByIdLocal });
  }

  // 3️⃣ Préparer les items custom
  const customItems = (getAllCustomItems() || []).map(it => ({
    ...it,
    name: it.name?.trim() || "",
    color: it.color || getMomentColor("Custom"),
    source: "custom"
  }));


  Object.entries(itemsMenus).forEach(([date, repasArray]) => {
    logDev(`📅 DATE = ${date}`);
    
    repasArray.forEach(r => {
      logDev(`  ⏰ MOMENT = ${r.moment}`);

    r.categories.forEach(c => {
      logDev(`    🗂️ CATEGORIE = ${c.categorie} | items = ${c.items.length}`);

    c.items.forEach((it, idx) => {
      logDev(`      ➜ ITEM ${idx}`, {
        _id: it._id,
        name: articlesByIdLocal[it.articleId]?.name,
        quantity: it.quantity,
        articleId: it.articleId,
        moment: r.moment,
        categorie: c.categorie,
        date
      });
      });
    });
  });
});

  logDev("🔥 FIN TRACE MENU COMPLET ------------------------");


  // 4️⃣ Flatten propre des menus journaliers
  const itemsMenusFlat = Object.entries(itemsMenus).flatMap(([date, repasArray]) =>
    repasArray.flatMap(r =>
      r.categories.flatMap(c =>
        c.items.map(it => {
          const articleId =
            typeof it.articleId === "object" ? it.articleId._id : it.articleId;

          const article = articlesByIdLocal[articleId] || {};

          return {
            articleId,
            name: it.articleId?.name || article.name  || it.name || it.quoi || "Sans nom",
            quantity: it.quantity || 1,
            unit: article.unit || "",
            date,
            moment: r.moment,
            categorie: c.categorie,
            source: "menu"
          };
        })
      )
    )
  );

  console.warn("🧪 ITEMS MENUS SANS ID =", 
  itemsMenusFlat.filter(it => !it.articleId)
);

  // 🔄 Normalisation : rendre les items custom compatibles avec itemsMenusFlat
  // - articleId virtuel = _id du custom item
  // - categorie/moment virtuels pour permettre la fusion
  const customItemsNormalized = customItems.map(it => ({ 
    articleId: it._id,              // 🔥 clé unique pour mergeItemsSources
    name: it.name,
    quantity: it.quantity || 1,
    unit: "",
    date: null,                     // pas lié à un jour
    moment: "Custom",               // moment virtuel
    categorie: "Divers",            // catégorie virtuelle
    source: "custom"
  }));

  // 5️⃣ Fusion menus + custom
  const tousLesItems = mergeItemsSources(itemsMenusFlat, customItemsNormalized, articlesByIdLocal);   
  logDev("🔥 FLATTEN =", itemsMenusFlat);
  logDev("🔥 ITEMS FUSIONNÉS =", tousLesItems);

  // 6️⃣ Construction des lignes finales pour le tableau
  const lignesFinales = buildTableRows(
    tousLesItems || [],
    contactsDominante || [],
    saisies || []
  );
  logDev("🔥 LIGNES FINALES =", lignesFinales);
    
  // 🔍 DEBUG : détecter les lignes sans catégorie
  lignesFinales.forEach((row, index) => {
    if (!row.theme || row.theme.trim() === "") {
      console.warn("⚠️ LIGNE SANS CATEGORIE :", {
        index,
        name: row.name,
        articleId: row.articleId,
        categorie: row.categorie,
        row
      });
    }
  });
  
  setRows(lignesFinales);

  // 7️⃣ Regroupement par catégorie pour la vue Cards
  const categoriesMap = {};

  lignesFinales.forEach(row => {
    const cat = row.quoi?.categorie || "Sans catégorie";
      if (!categoriesMap[cat]) {
        categoriesMap[cat] = {
          categorie: cat,
          totalItems: 0,
          itemsPris: 0
        };
      }

      categoriesMap[cat].totalItems += 1;
      
    // ✔ Article considéré comme pris si pris >= demandé
    if ((row.pris || 0) >= (row.quantite || 0)) {
      categoriesMap[cat].itemsPris += 1;
    }
  });

  setCategoriesCards(
    Object.values(categoriesMap).map(cat => ({
    ...cat,
    itemsRestants: cat.totalItems - cat.itemsPris
  }))
  );

    } catch (err) {
      logDev("❌ Erreur chargement rows", { message: err.message, stack: err.stack });
    }
  };

  // ------------------------------------------------------------
  // 🔁 Rechargement quand navigation / menus changent
  // ------------------------------------------------------------
  useEffect(() => {
    if (!groupeId) return;

    chargerRows();
  }, [location.pathname, location.search, itemsMenus, groupeId, groupeActif?.transmissionActive]);

  // ------------------------------------------------------------
  // 🔁 Rechargement quand les contacts dominants changent
  // ------------------------------------------------------------
  useEffect(() => {
    if (!groupeId) return;

    if (!contactsDominante?.length) return;
  chargerRows();
}, [contactsDominante]);


// ------------------------------------------------------------
// 🔄 Recalcul des catégories dès que rows change
// ------------------------------------------------------------
    useEffect(() => {
  if (!rows || rows.length === 0) return;

  const categoriesMap = {};

  rows.forEach(row => {
    const cat = row.quoi?.categorie || "Sans catégorie";

    if (!categoriesMap[cat]) {
      categoriesMap[cat] = {
        categorie: cat,
        totalItems: 0,
        itemsPris: 0
      };
    }

    categoriesMap[cat].totalItems += 1;

    if ((row.pris || 0) >= (row.quantite || 0)) {
      categoriesMap[cat].itemsPris += 1;
    }
  });

  setCategoriesCards(
    Object.values(categoriesMap).map(cat => ({
      ...cat,
      itemsRestants: cat.totalItems - cat.itemsPris
    }))
  );
}, [rows]);

  // ------------------------------------------------------------
  // 🏗️ Construction des colonnes du tableau
  // ------------------------------------------------------------
  useEffect(() => {
    if (!groupeId) return; 
    if (!contactsDominante?.length) return;

    setColumns(
      buildColumns({
        t,
        isMobile,
        moments: [],
        getTranslatedTheme,
        renderEditableCell,
        filteredContacts: contactsDominante,
        visibleContactIds
      })
    );
  }, [contactsDominante, visibleContactIds, t, isMobile]);

  // ------------------------------------------------------------
  // ✏️ Mise à jour d'une ligne (édition)
  // ------------------------------------------------------------
  const processRowUpdate = createItemRowUpdate(groupeId, setRows, () => chargerRows());

  const rowsFiltres = categorieFiltre === "Tout"
  ? rows
  : rows.filter(row => row.quoi?.categorie === categorieFiltre);

  const urlListe =
    `${window.location.origin}${location.pathname}${location.search}`;

      return (

        <>
        <div
        style={{       
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
            gap: "12px",
          marginBottom: isMobile ? "24px" : "48px"
        }}
        >

          {/* Toggle button */}
          <ListViewToggleButton
          vueCards={vueCards}
          setVueCards={setVueCards}
          isMobile={isMobile}
          t={(key) => t(key)}
          />
        </div>

        {Array.isArray(rows) && rows.length === 0 && (

          <Box

          sx={{
            textAlign: "center",
            padding: isMobile ? 2 : 4,
            backgroundColor: "#e4dcd2",
            borderRadius: 2,
            marginBottom: 2,
            color: "#555",
            fontSize: isMobile ? "0.9rem" : "1rem",
            fontStyle: "italic",
            maxWidth: isMobile ? "90%" : "auto",
            margin: "0 auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
        >

        {isAdmin
          ? `🛒 ${t("EmptyShoppingList")}`
          : `🛒 ${t("EmptyListeUsersMsg")}`}
      </Box>
    )}

        {vueCards ? (

          // 🔥 Vue Cards par catégorie
          <div                  
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr"
              : "repeat(auto-fill, minmax(220px, 1fr))",
            gap: "16px",
            marginTop: "16px",
            marginBottom: "24px"
          }}
        >

          {categoriesCards.map(cat => (

            <div                  
            key={cat.categorie}
            onClick={() => {
              setCategorieFiltre(cat.categorie);   // 👉 applique le filtre
              setVueCards(false);                  // 👉 bascule vers le tableau
            }}

            style={{
              padding: "12px",
              borderRadius: "8px",
              background: CATEGORY_COLORS[cat.categorie] || "#fffdf2",
              border: "1px solid #ddd",
              textAlign: "center",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              cursor: "pointer",          
              pointerEvents: "auto"       // 👉 débloque le clic si overlay
            }}
          >

            <div style={{ 
              fontWeight: "bold", 
              marginBottom: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px"
            }}
            >

              {CATEGORY_ICONS[cat.categorie] || null}
              {cat.categorie}

            </div>


            <div style={{ fontSize: "1.1rem" }}>
              {cat.itemsPris} / {cat.totalItems}
              </div>
          </div>    
        ))}

      </div>
      ) : (
      
        // 🔥 Vue Tableau classique
        <TableauListeItems     
        headerHeight={headerHeight}
        isMobile={isMobile}
        rows={rowsFiltres}
        columns={columns}
        filteredContacts={contactsDominante}
        visibleContactIds={visibleContactIds}
        setVisibleContactIds={setVisibleContactIds}
        processRowUpdate={processRowUpdate}
        getRowClassName={getRowClassName}
        emailsPeriodeDominante={emailsPeriodeDominante}
        t={t}
        onPrint={(contact) => handlePrint(contact, rows)}
        isAdmin={isAdmin}
        nomGroupe={nom}
        emojiGroupe={emoji}
        couleurGroupe={couleur}
        groupeId={groupeId}
        snackbar={snackbar}
        setSnackbar={setSnackbar}
        transmissionActive={groupeActif?.transmissionActive}
        categorieFiltre={categorieFiltre}
        setCategorieFiltre={setCategorieFiltre}
      />
    )}

    {isAdmin && groupeActif?.lienWhatsApp && (

      <div     
      style={{
      display: "flex",
      justifyContent: "center",
      marginTop: "24px",
      marginBottom: "24px"
    }}
  >

    <BoutonPartagerListeWhatsApp
    groupeActif={groupeActif}
    nomGroupe={nom}
    urlListe={urlListe}
    t={t}
    setSnackbar={setSnackbar}
    />
  </div>
)}

  <SnackbarFeedback
  snackbar={snackbar}
  setSnackbar={setSnackbar}
  emoji={emoji}
  couleur={couleur}
  />
  </>
  );
};

export default ListItems;
